import { Dispatch } from 'react';

export type TOrderDirection = 'asc' | 'desc';

export type TSortingPayload =
  | string
  | {
      field: string;
      direction?: TOrderDirection;
    };

export interface IPaginationState {
  filters: {
    [key: string]: string | string[] | null;
  };
  sorting: { [key: string]: TOrderDirection };
  page: number;
}

export const initialState: IPaginationState = {
  filters: {},
  sorting: {},
  page: 1,
};

export interface IAction {
  type:
    | keyof IPaginationState
    | 'reset'
    | 'clear-single-filter'
    | 'clear-sorting'
    | 'restore-from-url';
  payload: any;
}

export function convertObjToUrl(obj: IPaginationState): string {
  const { page, filters, sorting } = obj;
  return `&page=${page}${filterOptionsToParams(filters)}${sortingOptionsToParams(sorting)}`;
}

/**
 * Filters that identify which list a persisted URL state belongs to.
 * Sibling lists rendered on the same route (the debit / credit invoice tabs on
 * the customer profile) all write their state into the same query string, so
 * these keys are always taken from the list's own initial state and a URL
 * carrying different values is ignored - it was written by the sibling list.
 */
export const LIST_IDENTITY_FILTERS = ['type', 'customerId'];

/**
 * Builds the state a list should start with, restoring page/filters/sorting from
 * the current URL. Meant to be used as the `useReducer` initializer so the
 * restored state is already in place for the first request instead of triggering
 * a second one right after mount.
 */
export function resolveInitialStateFromUrl(
  initialPageState: IPaginationState,
  identityFilters: string[] = []
): IPaginationState {
  const urlState: IPaginationState = createUrlToObject(initialPageState)(window.location.search);

  const belongsToThisList = identityFilters.every((key) => {
    const urlValue = urlState.filters?.[key];

    return !urlValue || String(urlValue) === String(initialPageState.filters[key] ?? '');
  });

  if (!belongsToThisList) return initialPageState;

  return {
    ...urlState,
    filters: {
      ...urlState.filters,
      ...Object.fromEntries(identityFilters.map((key) => [key, initialPageState.filters[key]])),
    },
  };
}

export function reducer(state: IPaginationState, action: IAction): IPaginationState {
  const resetState = { ...state, page: 1 };

  const returnObj = (() => {
    if (action.type === 'reset') return initialState;

    if (action.type === 'clear-single-filter')
      return { ...resetState, filters: { ...state.filters, [action.payload]: undefined } };

    if (action.type === 'sorting')
      return { ...resetState, sorting: applySorting(action.payload, state.sorting) };

    if (action.type === 'clear-sorting') return { ...resetState, sorting: {} };

    if (action.type === 'page') return { ...resetState, page: action.payload };

    if (action.type === 'filters')
      return { ...resetState, filters: { ...state.filters, ...action.payload } };

    return action.payload;
  })();

  history.pushState({}, '', '?limit=10' + convertObjToUrl(returnObj));

  return returnObj;
}

export function createReducer(initialPageState: IPaginationState) {
  return function (state: IPaginationState, action: IAction): IPaginationState {
    const resetState = { ...state, page: 1 };

    const returnObj = (() => {
      if (action.type === 'reset') return initialPageState;

      if (action.type === 'clear-single-filter')
        return { ...resetState, filters: { ...state.filters, [action.payload]: undefined } };

      if (action.type === 'sorting')
        return { ...resetState, sorting: applySorting(action.payload, state.sorting) };

      if (action.type === 'clear-sorting') return { ...resetState, sorting: {} };

      if (action.type === 'page') return { ...resetState, page: action.payload };

      if (action.type === 'filters')
        return { ...resetState, filters: { ...state.filters, ...action.payload } };

      return action.payload;
    })();

    history.pushState({}, '', '?limit=10' + convertObjToUrl(returnObj));

    return returnObj;
  };
}

export function filterOptionsToParams(filters: IPaginationState['filters']) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (!value || value.length === 0) {
      return acc;
    }

    const encodedKey = encodeURIComponent(key);

    if (Array.isArray(value)) {
      const encoded = value.map((v) => encodeURIComponent(String(v))).join(',');
      return (acc += `&${encodedKey}=${encoded}`);
    }

    return (acc += `&${encodedKey}=${encodeURIComponent(String(value))}`);
  }, '');
}

export function sortingOptionsToParams(sorting: IPaginationState['sorting']) {
  return Object.entries(sorting).reduce(
    (acc, [key, value]) =>
      value ? (acc += `&sort=${encodeURIComponent(key)}&order=${encodeURIComponent(value)}`) : acc,
    ''
  );
}

export function applySorting(
  payload: TSortingPayload,
  currentSorting: IPaginationState['sorting']
): IPaginationState['sorting'] {
  if (typeof payload === 'string') {
    if (payload in currentSorting) {
      return {
        [payload]: currentSorting[payload] === 'asc' ? 'desc' : 'asc',
      };
    }

    return {
      [payload]: 'asc',
    };
  }

  const { field, direction } = payload;

  if (direction) {
    return {
      [field]: direction,
    };
  }

  if (field in currentSorting) {
    return {
      [field]: currentSorting[field] === 'asc' ? 'desc' : 'asc',
    };
  }

  return {
    [field]: 'asc',
  };
}

export function getSorting(sorting: IPaginationState['sorting']): {
  orderBy?: string | undefined;
  order?: TOrderDirection | undefined;
} {
  const objMap = Object.entries(sorting);

  if (!objMap.length) return {};

  return {
    orderBy: objMap.at(0)?.at(0),
    order: objMap.at(0)?.at(1) as TOrderDirection | undefined,
  };
}

export function dispatchPage(dispatch: Dispatch<IAction>) {
  return (payload: IPaginationState['page']) => dispatch({ type: 'page', payload });
}

export function dispatchSorting(dispatch: Dispatch<IAction>) {
  return (payload: string, _sortType?: string, direction?: TOrderDirection) =>
    dispatch({
      type: 'sorting',
      payload: direction
        ? {
            field: payload,
            direction,
          }
        : payload,
    });
}

export function dispatchClearSorting(dispatch: Dispatch<IAction>) {
  return () => dispatch({ type: 'clear-sorting', payload: null });
}

export function dispatchFilters(dispatch: Dispatch<IAction>) {
  return (payload: IPaginationState['filters']) => {
    // This prevents double load when no filters are applied on initial load.
    // TODO: we have to figure out different way to handle this.
    // if (Object.values(payload).every((p) => !p)) {
    //   return;
    // }

    dispatch({ type: 'filters', payload });
  };
}

export function urlToObject(url: string) {
  let result: any = {};

  try {
    result = url
      .slice(1)
      .split('&')
      .map((p) => p.split('='))
      .reduce((obj, [key, value]) => {
        // An empty search string splits into a single blank entry
        if (!key || key === 'limit') return obj;

        const decodedValue = value ? decodeURIComponent(value) : value;

        if (['sort', 'order'].includes(key))
          return {
            ...obj,
            sorting: {
              ...obj.sorting,
              [key]: decodedValue as TOrderDirection,
            },
          };

        if (key === 'page') return { ...obj, page: Number(decodedValue) };

        return { ...obj, filters: { ...obj.filters, [key]: decodedValue } };
      }, initialState);

    if (result.sorting.sort && result.sorting.order) {
      const actualSortingObj = { [result.sorting.sort]: result.sorting.order };
      result.sorting = actualSortingObj;
    }
  } catch (err) {
    console.error(err);
  }

  return result;
}

export function createUrlToObject(initialPageState: IPaginationState) {
  return function (url: string) {
    let result: any = {};

    try {
      result = url
        .slice(1)
        .split('&')
        .map((p) => p.split('='))
        .reduce((obj, [key, value]) => {
          // An empty search string splits into a single blank entry
          if (!key || key === 'limit') return obj;

          const decodedValue = value ? decodeURIComponent(value) : value;

          if (['sort', 'order'].includes(key))
            return {
              ...obj,
              sorting: {
                ...obj.sorting,
                [key]: decodedValue as TOrderDirection,
              },
            };

          if (key === 'page') return { ...obj, page: Number(decodedValue) };

          if (decodedValue && decodedValue.includes(',')) {
            return { ...obj, filters: { ...obj.filters, [key]: decodedValue.split(',') } };
          }

          return {
            ...obj,
            filters: { ...obj.filters, [key]: decodedValue },
          };
        }, initialPageState);

      if (result.sorting.sort && result.sorting.order) {
        const actualSortingObj = { [result.sorting.sort]: result.sorting.order };

        result.sorting = actualSortingObj;
      }
    } catch (err) {
      console.error(err);
    }

    return result;
  };
}
